/*
 * Copyright (c) 2020. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy
 */

import { Injectable } from '@angular/core';
import { MyNotificationModel } from '@app/models/my-notification.model';
import { ApiBaseService } from '@app/services/api-services/api-base.service';
import { HttpClient } from '@angular/common/http';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
@Injectable({
  providedIn: 'root',
})
export class MyNotificationListService extends ApiBaseService {
  private _myNotificationList: MyNotificationModel[] = [];
  protected _endpoint = 'mynotifications';

  constructor(http: HttpClient, authUserService: AuthUserService) {
    super(http, authUserService);
  }

  get myNotificationList(): MyNotificationModel[] {
    return this._myNotificationList;
  }

  set myNotificationList(value: MyNotificationModel[]) {
    this._myNotificationList = value;
  }

  /**
   *  Ryan Duffy
   *
   *  getNotificationByNotificationIdAndUserId
   *
   * : Get an notification based on the two ids by checking the list
   *
   * @param id of type string
   *
   * @returns Void: gets the notification with the id that meets the query
   */
  getNotificationByNotificationIdAndUserId(
    notificationId: string,
    userId: string
  ): MyNotificationModel {
    for (const notification of this.myNotificationList) {
      if (notification.notificationId === notificationId && notification.userId === userId) {
        return notification;
      }
    }
  }

  /**
   *  Ryan Duffy
   *
   *  getNotificationByUserId
   *
   * : Get an notification user based on the id by checking the list
   *
   * @param id of type string
   *
   * @returns Void: gets the user with the id that meets the query
   */
  getNotificationByUserId(userId: string): MyNotificationModel {
    for (const notification of this.myNotificationList) {
      if (notification.userId[0] === userId) {
        return notification;
      }
    }
    return null;
  }
  /**
   *  Ryan Duffy
   *
   *  getMyNotificationsByUserID
   *
   * : Gets a list of notifications based on the associated userID
   *
   * @param id of type string
   *
   * @returns Void: gets the notifications with the matching UUID
   */
  getMyNotificationsByUserID(userId: string): MyNotificationModel[] {
    const list = [];
    for (const myNotification of this.myNotificationList) {
      if (myNotification.userId === userId) {
        list.push(myNotification);
      }
    }
    return list;
  }
  /**
   *  Ryan Duffy
   *
   *  getMyNotificationByNotificationId
   *
   * : Gets a list of notifications based on the associated userID
   *
   * @param id of type string
   *
   * @returns Void: gets the notifications with the matching UUID
   */
  getMyNotificationByNotificationId(notificationId: string): MyNotificationModel {
    for (const myNotification of this.myNotificationList) {
      if (myNotification.notificationId === notificationId) {
        return myNotification;
      }
    }
  }

  /**
   *  Ryan Duffy
   *
   *  getMyNewNotificationByNotificationId
   *
   * : Gets a list of notifications based on the associated userID
   *
   * @param id of type string
   *
   * @returns Void: gets the notifications with the matching UUID
   */
  getMyNewNotificationByUserId(userId: string): MyNotificationModel[] {
    const list = [];
    for (const myNotification of this.myNotificationList) {
      if (
        myNotification.userId === userId &&
        !myNotification.notificationDetails.notificationRead
      ) {
        list.push(myNotification);
      }
    }
    return list;
  }

  /**
   *  Ryan Duffy
   *
   *  addtoList
   *
   * : handles the insert of a new notification into the list
   *
   * @param data of type MyNotificationModel
   *
   * @returns Void: inserts a new notification into the lsit
   */
  addToList(data: MyNotificationModel): void {
    this.myNotificationList.push(data);
  }
  /**
   *  Ryan Duffy
   *
   *  removeFromList
   *
   * : deletes an notification from the list
   *
   * @param data of type MyNotificationModel
   *
   * @returns Void: deletes a notification
   */
  removeFromList(data: MyNotificationModel): void {
    let index = 0;
    for (const notification of this.myNotificationList) {
      if (notification.notificationId === data.notificationId) {
        this.myNotificationList.splice(index, 1);
      }
      index++;
    }
  }
  /**
   *  Ryan Duffy
   *
   *  updateInList
   *
   * : handles the update of an notification in the list
   *
   * @param data of type notificationmodel
   *
   * @returns Void: update the list with the data
   */
  updateInList(data: MyNotificationModel): void {
    for (let notification of this.myNotificationList) {
      if (notification.notificationId === data.notificationId) {
        notification = data;
        break;
      }
    }
  }
  /**
   *  Ryan Duffy
   *
   *  handleData
   *
   * : handles the data from the list by extending the handleData function from the API base service
   *
   * @param data of type MyNotificationModel
   *
   * @returns Void: get the notification from the postgres database and create the list
   */
  protected handleData(data: any): void {
    this.myNotificationList = [];
    let notification: MyNotificationModel;
    super.handleData(data);
    for (const item of data) {
      notification = new MyNotificationModel();
      this.addToList(notification.fromJson(item));
    }
  }
}
